import { fullStoryTracking, type FullStoryEvent } from '~/utils/fullstory/tracking';

import { newRelic } from '../newRelic/newRelic';
import SegmentMonitor from './SegmentMonitor';
import Events, { FullStoryEventsToBeTracked, type Traits } from './schema';

/**
 * analytics.identify
 * @param id
 * @param traits
 */
const identify = (id: number | string | undefined, traits?: unknown): void => {
  if (window.analytics) {
    window.analytics.identify(id, traits as never);
  } else {
    newRelic.log('Segment identify called before window.analytics was available', {
      level: 'WARN',
    });
  }
};

/**
 * analytics.track
 * @param name
 * @param properties
 */
const track = <T>(name: string, properties?: T): Promise<void> | void => {
  // @NOTE: we conditionally send some events to FullStory from the clientside to test funnels
  if (FullStoryEventsToBeTracked.includes(name as Events))
    // @ts-expect-error - this is temporary until we have explicit events for FullStory
    fullStoryTracking(name as FullStoryEvent, properties);

  if (window.analytics) return window.analytics.track(name, properties as never);
  else
    newRelic.log('Segment track called before window.analytics was available', { level: 'WARN' });
};

/**
 * analytics.page
 * @param url
 */
const page = (url: string): void => {
  if (window.analytics) {
    window.analytics.page(url, {
      screen: {
        height: window.screen.height,
        width: window.screen.width,
      },
      window: {
        height: window.innerHeight,
        width: window.innerWidth,
      },
    });
  } else {
    newRelic.log('Segment page called before window.analytics was available', { level: 'WARN' });
  }
};

/**
 * Get local version of analytics.js traits from localstorage
 */
export const getLocalTraits = (): Traits | undefined => {
  const ajsUserTraits = localStorage.getItem('ajs_user_traits');
  if (ajsUserTraits) {
    try {
      return JSON.parse(ajsUserTraits);
    } catch (error) {
      return undefined;
    }
  } else {
    return undefined;
  }
};

/**
 * Is a trait different from the current trait saved
 * @param property
 * @param value
 */
export const isTraitDifferent = (property: string, value: unknown) => {
  const localTraits = getLocalTraits();
  return !(localTraits && localTraits[property as keyof typeof localTraits] === value);
};

export { Events, SegmentMonitor, identify, page, track };
